import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Badge, ProgressBar } from 'react-bootstrap';

import FancyButton from '../ui/fancyButton';
import CourseDetailsModal from './CourseDetailsModal';
import './CourseCard.css';
import { selectAuthUserId } from '../../redux/authSlice';
import { setPreviewLesson } from '../../redux/classroomSlice';

const CourseCard = ({
  course,
  onSelect,
  onStartAnimated,
  onStartScene,
  onStartExam,
  onFork,
  onDelete,
  isAdded = false,
  autoOpen = false,
  onAutoOpened = null,
}) => {
  const dispatch = useDispatch();
  const [showDetails, setShowDetails] = useState(false);
  const [launchMode, setLaunchMode] = useState('classroom');

  const currentUserId = useSelector(selectAuthUserId);
  const isOwner = course.owner_id === currentUserId;
  const courseDifficulty = course.difficultyLevel || course.difficulty_level || 'Beginner';

  const progress = Math.round((course.currentLessonIndex / (course.units?.length || 1)) * 100) || 0;

  useEffect(() => {
    if (autoOpen) {
      setShowDetails(true);
      if (typeof onAutoOpened === 'function') {
        onAutoOpened(course.id);
      }
    }
  }, [autoOpen, course.id, onAutoOpened]);

  // 📌 New: Share handlers
  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/courses/${course.id}`;
    navigator.clipboard.writeText(shareUrl);
    alert('Link copied to clipboard!');
  };
  const handleJumpToLesson = (index) => {
    const lesson = course.units?.[index];
    dispatch(setPreviewLesson(lesson));
  };

  const handleStart = (e) => {
    e.stopPropagation();
    if (launchMode === 'animation') {
      if (typeof onStartAnimated === 'function') onStartAnimated(course);
      return;
    }
    if (launchMode === 'scene') {
      if (typeof onStartScene === 'function') onStartScene(course);
      return;
    }
    if (launchMode === 'exam') {
      if (typeof onStartExam === 'function') onStartExam(course);
      return;
    }
    if (typeof onSelect === 'function') onSelect(course);
  };

  return (
    <>
      <Card
        className="course-card mb-4 shadow-sm"
        onClick={() => setShowDetails(true)}
        style={{ cursor: 'pointer' }}
      >
        <Card.Body>
          {/* 1. Title */}
          <h4 className="mb-2">{course.title}</h4>

          {/* 2. Difficulty Badge */}
          <div className="mb-3">
            <Badge
              bg={
                courseDifficulty?.toLowerCase() === 'beginner'
                  ? 'success'
                  : courseDifficulty?.toLowerCase() === 'intermediate'
                    ? 'warning'
                    : 'danger'
              }
            >
              {courseDifficulty}
            </Badge>
          </div>

          {/* 3. Course Description */}
          <Card.Text className="course-description mb-3">{course.description}</Card.Text>

          {/* 4. Number of Lessons & Total Hours */}
          <div className="mb-3">
            <small className="text-muted">
              <i className="fa-solid fa-book-open me-1" />
              {course.units?.length || 0} lessons
            </small>
            <span className="mx-2">•</span>
            <small className="text-muted">
              <i className="fa-solid fa-clock me-1" />
              {course.duration || 0} hours
            </small>
          </div>

          {/* 5. Progress Bar (always render div to maintain grid) */}
          <div className="mb-3">
            {isOwner && (
              <>
                <small className="text-muted">Progress: {progress}%</small>
                <ProgressBar now={progress} variant="info" />
              </>
            )}
          </div>

          {/* 6. Button for Start or Continue */}
          <div className="d-flex justify-content-between align-items-center">
            <div>
              {isOwner ? (
                <div className="course-launch-controls">
                  <select
                    className="course-launch-mode"
                    value={launchMode}
                    onChange={(e) => setLaunchMode(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Mode"
                  >
                    <option value="classroom">Text</option>
                    <option value="animation">Visual</option>
                    <option value="scene">Scene</option>
                    <option value="exam">Exam</option>
                  </select>
                  <FancyButton variant="secondary" onClick={handleStart}>
                    {progress === 0 ? 'Start' : 'Continue'}
                  </FancyButton>
                </div>
              ) : onFork ? (
                <FancyButton
                  variant={isAdded ? 'secondary' : 'primary'}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof onFork === 'function') {
                      onFork(course);
                    } else {
                      // eslint-disable-next-line no-console
                      console.error('onFork is not a function:', onFork);
                    }
                  }}
                  disabled={isAdded}
                >
                  {isAdded ? 'Already Added' : 'Add Course'}
                </FancyButton>
              ) : null}
            </div>

            {/* Action buttons on the right */}
            <div className="d-flex gap-2">
              {/* Share button */}
              <FancyButton
                size="sm"
                variant="outline-info"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyLink();
                }}
                style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                title="Share course link"
              >
                <i className="fa-solid fa-share-nodes" aria-hidden="true" />
              </FancyButton>

              {/* Delete button (only for owners) */}
              {isOwner && onDelete && (
                <FancyButton
                  size="sm"
                  variant="outline-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(course);
                  }}
                  style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                  title="Delete course"
                >
                  <i className="fa-solid fa-trash" aria-hidden="true" />
                </FancyButton>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      <CourseDetailsModal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        course={course}
        onStart={onSelect}
        onStartAnimated={onStartAnimated}
        onStartScene={onStartScene}
        onStartExam={onStartExam}
        onFork={onFork}
        onJumpToLesson={handleJumpToLesson}
      />
    </>
  );
};

export default CourseCard;
